import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import isNil from 'lodash/isNil';
import Utils from '../utils';
import MetadataUtils from '../utils/utils-metadata';
import { mapProductFormatToLabel, ProductMetaData } from '../../../models/Product';
import { useLanguage } from '../../contexts/language';
import { LoanFormat, mapLoanFormat } from '../../../models/LoanFormat';
import { formatLoanFormat } from '../utils/formatLoanFormat';
import { formatProductType } from '../utils/formatProductType';
import { parseProductType, ProductType } from '../../../models/ProductType';
import { useTranslation } from 'react-i18next';

type Props = {
    readonly product: ProductMetaData;
    readonly loanFormatKey: string;
    readonly showIcon?: boolean;
};

export const ProductFormat: FC<Props> = props => {
    const loanFormat = mapLoanFormat(props.product.loanFormat);
    switch (loanFormat) {
        case LoanFormat.eBooks:
            return <EBookProductFormat {...props} />;
        case LoanFormat.eAudiobooks:
            return <EAudiobookProductFormat {...props} />;
        case LoanFormat.eMagazines:
        case LoanFormat.ePress:
            return <EPressProductFormat {...props} />;
        default:
            return null;
    }
};

export const EBookProductFormat: FC<Props> = ({ product, showIcon, loanFormatKey }) => {
    const language = useLanguage();
    const { t } = useTranslation();

    const productFormatKey = mapProductFormatToLabel(product.productFormat!);
    const filter = { illustrated: { include: ['true'] } };
    const linkTo = MetadataUtils.createMetaDataLink(product.productClass, filter, { loanFormatKey, language });
    const isIllustrated = !isNil(product.illustrated) && product.illustrated === true;

    return (
        <p>
            {showIcon ? <span className="ebook" /> : null}
            {t(`components.productFormat.eBook.${productFormatKey}`, {
                pages: product.pages,
                defaultValue: t('components.productFormat.eBook.none')
            })}
            &nbsp;
            {isIllustrated ? <Link to={linkTo}>illustrated</Link> : null}
        </p>
    );
};

export const EAudiobookProductFormat: FC<Props> = ({ product, showIcon, loanFormatKey }) => {
    const language = useLanguage();
    const { t } = useTranslation();

    const filter = { productionFormat: { include: [product.productionFormat] } };
    const linkTo = MetadataUtils.createMetaDataLink(product.productClass, filter, { loanFormatKey, language });
    const duration = Utils.formatDuration(product.duration, { excludeSeconds: true, format: 'short' });

    return (
        <p>
            {showIcon ? <span className="eaudiobook" /> : null}
            {t('components.productFormat.eAudiobook.duration', {
                minutes: t('components.productFormat.eAudiobook.minutes', {
                    count: duration['minutes'],
                    defaultValue: ''
                }),

                hours: t('components.productFormat.eAudiobook.hours', {
                    count: duration['hours'],
                    defaultValue: ''
                })
            })}
            <br />
            <Link to={linkTo}>
                {t(`filters:productionFormat.${product.productionFormat}`, {
                    defaultValue: ''
                })}
            </Link>
        </p>
    );
};

/**
 * ePress
 */

export function formatEPressLabel(product: ProductMetaData) {
    const loanFormat = mapLoanFormat(product.loanFormat)!;
    const formatLabel = formatLoanFormat(loanFormat, 'singular');

    const productTypeLabel = product?.productTypes
        ?.map(item => parseProductType(item))
        .filter((item): item is ProductType => item !== undefined)
        .map(item => formatProductType(item, 'singular'))
        .join('/');

    return formatLabel + (productTypeLabel ? ` (${productTypeLabel})` : '');
}

export const EPressProductFormat: FC<Props> = ({ product, showIcon, loanFormatKey }) => {
    const language = useLanguage();

    const filter = { productionFormat: { include: [product.productionFormat] } };
    const linkTo = MetadataUtils.createMetaDataLink(product.productClass, filter, { loanFormatKey, language });

    return (
        <p>
            {showIcon ? <span className="emagazine" /> : null}
            {formatEPressLabel(product)}
            <br />
            <Link to={linkTo}>{product.productionFormat}</Link>
        </p>
    );
};
